import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { PageHeader, Segmented } from '../ui';
import { reportsApi } from './api';
import { DutyGridView } from './DutyGridView';
import { ExportButton } from './ExportButton';
import { useReport, type ReportPageProps } from './frame';
import { PeriodRow } from './PeriodRow';
import { ShiftBookDriver } from './ShiftBookDriver';
import { ShiftBookFleet } from './ShiftBookFleet';

const VIEWS = ['fleet', 'grid'] as const;

type ShiftBookView = (typeof VIEWS)[number];

/**
 * What each driver worked, what it cost, and what is still owed.
 *
 * Three screens rather than one: the fleet table, the duty grid, and one driver's page,
 * which is reached from either of the other two and is the only place a payment is recorded.
 * The period is the frame's, so opening a driver from a month's grid shows that driver's
 * month.
 */
export function ShiftBookPage({ frame }: ReportPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [view, setView] = useState<ShiftBookView>('fleet');
  const [driverId, setDriverId] = useState<string | null>(null);

  // Which day to open expanded, when the driver page was reached by clicking a grid cell.
  // Clicking a shaded square and landing on a page that does not mention that day is the one
  // thing the grid must not do.
  const [openDay, setOpenDay] = useState<string | null>(null);

  const onFleet = driverId === null && view === 'fleet';
  const onGrid = driverId === null && view === 'grid';

  const fleet = useReport(frame, onFleet ? (period) => reportsApi.fleet(period) : null, 'fleet');
  const grid = useReport(frame, onGrid ? (period) => reportsApi.grid(period) : null, 'grid');

  const driver = useReport(
    frame,
    driverId === null ? null : (period) => reportsApi.driver(driverId, period),
    driverId ?? '',
  );

  /** The path the export button downloads, which is whatever is on screen. */
  const exportPath =
    driverId !== null
      ? `/api/tenant/reports/shift-book/${driverId}`
      : view === 'grid'
        ? '/api/tenant/reports/shift-book/grid'
        : '/api/tenant/reports/shift-book';

  return (
    <>
      <PageHeader
        title={t('nav.reports.shift-book')}
        description={t('reports.about.shiftBook')}
        actions={
          <>
            {driverId === null && (
              <Segmented
                label={t('reports.shiftBook.views')}
                value={view}
                onChange={setView}
                options={VIEWS.map((name) => ({
                  value: name,
                  label: t(`reports.shiftBook.view.${name}`),
                }))}
              />
            )}

            <ExportButton frame={frame} path={exportPath} />
          </>
        }
      />

      <PeriodRow frame={frame} />

      {driverId !== null && driver.data !== null && (
        <ShiftBookDriver
          report={driver.data}
          openDay={openDay}
          onBack={() => {
            setDriverId(null);
            setOpenDay(null);
          }}
          onSettings={() => void navigate('/settings/duty')}
        />
      )}

      {onFleet && fleet.data !== null && (
        <ShiftBookFleet
          report={fleet.data}
          onOpenDriver={(id) => {
            setDriverId(id);
            setOpenDay(null);
          }}
          onSettled={fleet.reload}
        />
      )}

      {onGrid && grid.data !== null && (
        <DutyGridView
          grid={grid.data}
          onOpenDriver={(id, date) => {
            setDriverId(id);
            setOpenDay(date);
          }}
        />
      )}
    </>
  );
}
